import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import "../../ForBrands/brandMode.css";
import { Badge, Btn, Card, ConfirmModal, ErrorState, Loading, Overline } from "../../ForBrands/ui/BrandUI";
import Icon from "../../ForBrands/ui/icons";
import GameHeaderForm from "./GameHeaderForm";
import GameList, { GameStatusBadge } from "./GameList";
import GameStatsGrid from "./GameStatsGrid";
import SeasonTotals from "./SeasonTotals";
import { adminGamesUrl, makeGameStatsApi } from "./gameStatsServices";
import {
  MESSAGES,
  bodyFromDraft,
  clearServerCell,
  deletedMessage,
  describeRefresh,
  draftFromGame,
  draftSnapshot,
  editedText,
  errorsFromServer,
  gameDateText,
  gameTitle,
  isBasketball,
  localToday,
  mergeErrors,
  newDraft,
  pointsSum,
  refreshBusy,
  savedMessage,
  validateDraft,
} from "./gameStatsModel";

// One CACHE Partner team's games (Jayson, 10/3): the season's games and
// each player's season totals, and the game editor (the game's details, the
// box score sheet, then Save draft, Save final, Delete game and Cancel).
// Admins enter games for now; a coach page can mount the same page later
// with its own `api`.
//
// Props:
//   teamId    the cache_teams team_id (the admin API is built from it)
//   api       services to use instead (gameStatsServices.makeGameStatsApi's
//             shape: list, get, create, update, remove, refresh)
//   header    a node shown above everything (the mount's back link, title)
//   canEdit   overrides the server's canEdit
//   onDirtyChange(dirty) tells the mount when the editor holds unsaved edits
//   pollMs, today  tests only: the score update poll and today's date
//
// Saving runs the score update on the server (the athletes holding the
// players' rows are rescored in the background). While it is queued or
// running, the page asks again every 10 seconds.

export const POLL_MS = 10000;

const cx = (...parts) => parts.filter(Boolean).join(" ");
const IDLE = { state: "idle" };
const NO_SERVER_ERRORS = { fields: {}, lines: {}, message: "" };

// Which server field a header field answers to.
const FIELD_OF = {
  date: "date",
  opponent: "opponent",
  homeAway: "home_away",
  city: "location",
  state: "location",
  competition: "competition",
  teamScore: "team_score",
  opponentScore: "opponent_score",
  status: "status",
};

const DOT = {
  busy: "animate-pulse bg-bm-orange",
  done: "bg-bm-positive",
  failed: "bg-bm-critical",
  idle: "bg-bm-tertiary",
};

// The score update's state, and "Update scores now" when it is not running.
const ScoreUpdateBar = ({ refresh, canEdit, onRefresh, starting }) => {
  const info = describeRefresh(refresh);
  const sub = info.detail;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-bm-border bg-bm-subtle px-5 py-3">
      <div className="flex min-w-0 items-start gap-3">
        <span aria-hidden="true" className={cx("mt-[6px] h-2 w-2 shrink-0 rounded-full", DOT[info.tone])} />
        <div role="status" aria-live="polite" className="min-w-0">
          <p className={cx("bm-body-m", info.tone === "failed" ? "text-bm-critical" : "text-bm-ink")}>{info.text}</p>
          {sub && <p className="bm-body-s text-bm-muted">{sub}</p>}
        </div>
      </div>
      {canEdit && !info.busy && (
        <Btn variant="secondary" size="sm" onClick={onRefresh} disabled={starting}>
          <Icon name="refresh" size={13} className={cx("mr-1.5", starting && "animate-spin")} />
          {starting ? "Starting..." : "Update scores now"}
        </Btn>
      )}
    </div>
  );
};

const unique = (list) => [...new Set(list.filter((item) => typeof item === "string" && item.trim()))];

const TeamGamesPage = ({ teamId, api, header = null, canEdit: canEditProp, onDirtyChange, pollMs = POLL_MS, today: todayProp }) => {
  const fallbackApi = useMemo(() => makeGameStatsApi({ baseUrl: adminGamesUrl(teamId) }), [teamId]);
  // Read through a ref, so a mount that builds its services on every render
  // never reloads the page.
  const apiRef = useRef(api || fallbackApi);
  apiRef.current = api || fallbackApi;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // {text, retry}: a load that failed, and whether another try can help.
  const [loadError, setLoadError] = useState(null);
  const [season, setSeason] = useState(null);
  const [refresh, setRefresh] = useState(IDLE);
  const [starting, setStarting] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  // The editor: {draft, base (the snapshot it opened or last saved with), game}.
  const [editor, setEditor] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState(NO_SERVER_ERRORS);
  const [formError, setFormError] = useState(null);
  const [saveWarnings, setSaveWarnings] = useState([]);
  const [saving, setSaving] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const seasonRef = useRef(null);
  const ticket = useRef(0);
  const alive = useRef(true);
  const editorRef = useRef(null);

  const today = todayProp || localToday();
  const team = data?.team || null;
  const roster = useMemo(() => data?.roster || [], [data]);
  const editable = typeof canEditProp === "boolean" ? canEditProp : Boolean(data?.canEdit);
  const blocked = team && !isBasketball(team) ? MESSAGES.notBasketball : "";

  const applyList = useCallback((res) => {
    const next = res || {};
    setData(next);
    seasonRef.current = next.season || null;
    setSeason(next.season || null);
    setRefresh(next.refresh || IDLE);
  }, []);

  // `quiet` reloads after a save or delete keep the page as it is.
  const load = useCallback(
    async (label, { quiet = false } = {}) => {
      const mine = ++ticket.current;
      if (!quiet) {
        setLoading(true);
        setLoadError(null);
      }
      try {
        const res = await apiRef.current.list(label || undefined);
        if (alive.current && mine === ticket.current) applyList(res);
      } catch (err) {
        if (alive.current && mine === ticket.current && !quiet) {
          // A refusal (another sport, no access) stays one; a dropped
          // connection or a server error is worth another try.
          const status = Number(err?.status) || 0;
          setLoadError({ text: err?.message || "Could not load the games. Try again.", retry: !status || status >= 500 || status === 408 || status === 429 });
        }
      } finally {
        if (alive.current && mine === ticket.current) setLoading(false);
      }
    },
    [applyList],
  );

  useEffect(() => {
    alive.current = true;
    setData(null);
    setRefresh(IDLE);
    setEditor(null);
    setConfirm(null);
    seasonRef.current = null;
    load(null);
    return () => {
      alive.current = false;
    };
  }, [teamId, load]);

  // Ask again while the score update is queued or running.
  useEffect(() => {
    if (!refreshBusy(refresh)) return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await apiRef.current.list(seasonRef.current || undefined);
        if (!alive.current) return;
        // The admin may have picked another season meanwhile: then only the
        // update's state is news.
        if (!seasonRef.current || !res?.season || res.season === seasonRef.current) applyList(res);
        else setRefresh(res?.refresh || IDLE);
      } catch (err) {
        if (alive.current) setPollTick((n) => n + 1);
      }
    }, pollMs);
    return () => clearTimeout(timer);
  }, [refresh, pollTick, pollMs, applyList]);

  const dirty = useMemo(() => Boolean(editor) && draftSnapshot(editor.draft) !== editor.base, [editor]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Closing or reloading the tab asks first while there are unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const check = useMemo(() => (editor ? validateDraft(editor.draft, { team, today }) : null), [editor, team, today]);

  const resetEditorState = () => {
    setAttempted(false);
    setServerErrors(NO_SERVER_ERRORS);
    setFormError(null);
    setSaveWarnings([]);
  };

  const openEditor = (draft, game) => {
    setEditor({ draft, base: draftSnapshot(draft), game: game || null });
    resetEditorState();
    // The editor takes the list's place; bring its top into view.
    setTimeout(() => {
      const node = editorRef.current;
      if (node && typeof node.scrollIntoView === "function" && node.getBoundingClientRect().top < 0) node.scrollIntoView({ block: "start" });
    }, 0);
  };
  const closeEditor = () => {
    setEditor(null);
    resetEditorState();
  };

  // Run `then` now, or after the admin agrees to drop unsaved edits.
  const leave = (then) => (dirty ? setConfirm({ kind: "leave", then }) : then());

  const changeSeason = (label) => {
    seasonRef.current = label;
    setSeason(label);
    load(label);
  };

  const setDraft = (draft) => setEditor((current) => (current ? { ...current, draft } : current));

  const onHeaderChange = (header, keys = []) => {
    setEditor((current) => (current ? { ...current, draft: { ...current.draft, header } } : current));
    setSaveWarnings([]);
    setServerErrors((s) => {
      const fields = { ...s.fields };
      keys.forEach((key) => delete fields[FIELD_OF[key] || key]);
      return { ...s, fields };
    });
  };

  const onRowsChange = (rows, change = {}) => {
    setEditor((current) => (current ? { ...current, draft: { ...current.draft, rows } } : current));
    setSaveWarnings([]);
    setServerErrors((s) => {
      const fields = { ...s.fields };
      delete fields.lines;
      if (change.paste) return { ...s, fields, lines: {} };
      if (change.key === "dnp" && change.playerId) {
        const lines = { ...s.lines };
        delete lines[change.playerId];
        return { ...s, fields, lines };
      }
      return change.playerId ? { ...clearServerCell(s, change.playerId, change.key), fields } : { ...s, fields };
    });
  };

  const save = async (status) => {
    if (!editor || saving) return;
    const game = editor.game;
    const draft = { ...editor.draft, header: { ...editor.draft.header, status } };
    setDraft(draft);
    setAttempted(true);
    setServerErrors(NO_SERVER_ERRORS);
    setFormError(null);
    setSaveWarnings([]);
    if (!validateDraft(draft, { team, today }).ok) {
      setFormError({ text: MESSAGES.fix });
      return;
    }
    setSaving(status);
    try {
      const body = bodyFromDraft(draft);
      const res = game ? await apiRef.current.update(game.game_id, body) : await apiRef.current.create(body);
      if (!alive.current) return;
      const saved = res?.game || null;
      // The editor shows the game as saved, unless the admin kept typing
      // while it saved: then their newer edits stay, still unsaved.
      const sent = draftSnapshot(draft);
      setEditor((current) => {
        const next = saved ? draftFromGame(saved, roster) : draft;
        const base = draftSnapshot(next);
        if (!current || draftSnapshot(current.draft) === sent) return { draft: next, base, game: saved || current?.game || null };
        return { ...current, base, game: saved || current.game };
      });
      setAttempted(false);
      setSaveWarnings(unique(Array.isArray(res?.warnings) ? res.warnings : []));
      if (res?.refresh) setRefresh(res.refresh);
      toast.success(savedMessage(saved || { opponent: draft.header.opponent }));
      // The list and totals catch up; a game saved in another season shows
      // that season.
      const label = saved?.season || seasonRef.current;
      seasonRef.current = label;
      setSeason(label);
      load(label, { quiet: true });
    } catch (err) {
      if (!alive.current) return;
      const answer = err?.data || {};
      if (answer.error === "invalid") {
        setServerErrors(errorsFromServer(answer));
        setFormError({ text: answer.message || MESSAGES.fix });
      } else if (err?.status === 409) {
        setFormError({ text: err.message, gameId: answer.game_id || null });
      } else {
        setFormError({ text: err?.message || "Could not save the game. Try again." });
      }
    } finally {
      if (alive.current) setSaving(null);
    }
  };

  // The game a duplicate answer points at.
  const openSaved = (gameId) =>
    leave(async () => {
      const known = (data?.games || []).find((g) => g.game_id === gameId);
      if (known) {
        openEditor(draftFromGame(known, roster), known);
        return;
      }
      try {
        const res = await apiRef.current.get(gameId);
        if (alive.current && res?.game) openEditor(draftFromGame(res.game, roster), res.game);
      } catch (err) {
        if (alive.current) toast.error(err?.message || "Could not open that game. Try again.");
      }
    });

  const removeGame = async () => {
    const game = editor?.game;
    if (!game || deleting) return;
    setDeleting(true);
    try {
      const res = await apiRef.current.remove(game.game_id);
      if (!alive.current) return;
      toast.success(deletedMessage(game));
      if (res?.refresh) setRefresh(res.refresh);
      setConfirm(null);
      closeEditor();
      load(seasonRef.current, { quiet: true });
    } catch (err) {
      if (!alive.current) return;
      setConfirm(null);
      toast.error(err?.message || "Could not delete the game. Try again.");
    } finally {
      if (alive.current) setDeleting(false);
    }
  };

  const updateScores = async () => {
    setStarting(true);
    try {
      const res = await apiRef.current.refresh();
      if (alive.current && res?.refresh) setRefresh(res.refresh);
    } catch (err) {
      if (alive.current) toast.error(err?.message || "Could not start the score update. Try again.");
    } finally {
      if (alive.current) setStarting(false);
    }
  };

  let content;
  if (!data && loading) {
    content = <Loading label="Loading games..." />;
  } else if (!data) {
    content = (
      <ErrorState role="alert" className="flex flex-wrap items-center justify-between gap-3">
        <span>{loadError?.text || "Could not load the games. Try again."}</span>
        {(!loadError || loadError.retry) && (
          <Btn variant="secondary" size="sm" onClick={() => load(seasonRef.current)}>
            Try again
          </Btn>
        )}
      </ErrorState>
    );
  } else if (editor) {
    const h = editor.draft.header;
    const shown = attempted ? mergeErrors(check, serverErrors) : { fields: serverErrors.fields, lines: serverErrors.lines };
    const cellWarnings = attempted ? check.cellWarnings : {};
    const notes = unique([...saveWarnings, ...check.warnings]);
    const title = editor.game || h.opponent.trim() ? gameTitle(team, { opponent: h.opponent || editor.game?.opponent }) : "New game";
    const meta = editor.game
      ? `Last saved ${editedText(editor.game)}`
      : [team?.league_label, team?.season].filter(Boolean).join(" · ");
    const busy = Boolean(saving) || deleting;
    content = (
      <Card padding="p-0" className="overflow-hidden">
        <div ref={editorRef} className="flex flex-wrap items-start justify-between gap-3 border-b border-bm-border px-5 py-4">
          <div className="min-w-0">
            <Overline>{editor.game ? (editable ? "Edit game" : "Game") : "New game"}</Overline>
            <h2 className="mt-1 bm-h-s text-bm-ink">{title}</h2>
            {meta && <p className="mt-[2px] bm-body-s text-bm-muted">{meta}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {editor.game ? <GameStatusBadge status={editor.game.status} /> : <Badge tone="passive">Not saved</Badge>}
            {dirty && <Badge tone="soft">Unsaved changes</Badge>}
          </div>
        </div>
        <section aria-label="Game details" className="px-5 py-5">
          <GameHeaderForm
            header={h}
            onChange={onHeaderChange}
            team={team}
            errors={shown.fields}
            readOnly={!editable}
            pointsSum={pointsSum(editor.draft.rows)}
            today={today}
          />
          {shown.fields.lines && (
            <p role="alert" className="mt-4 bm-body-s text-bm-critical">
              {shown.fields.lines}
            </p>
          )}
        </section>
        {editable && notes.length > 0 && (
          <div className="mx-5 mb-5 rounded-[8px] border border-bm-attention bg-bm-attention/10 px-4 py-3">
            <p className="bm-label-m text-bm-ink">{saveWarnings.length ? "Saved. A few things to check:" : "A few things to check:"}</p>
            <ul className="mt-1 list-disc pl-5">
              {notes.map((note) => (
                <li key={note} className="bm-body-s text-bm-n700">
                  {note}
                </li>
              ))}
            </ul>
          </div>
        )}
        <section aria-label="Box score" className="border-t border-bm-border px-5 py-5">
          <h3 className="mb-2 bm-h-xs text-bm-ink">Box score</h3>
          <GameStatsGrid
            rows={editor.draft.rows}
            onChange={onRowsChange}
            errors={shown.lines}
            warnings={cellWarnings}
            readOnly={!editable}
            label={`Box score, ${title}`}
          />
        </section>
        {formError && (
          <div className="px-5 pb-4">
            <ErrorState role="alert" className="flex flex-wrap items-center gap-3">
              <span>{formError.text}</span>
              {formError.gameId && (
                <Btn variant="link" size="sm" className="!h-auto !px-0" onClick={() => openSaved(formError.gameId)}>
                  Open the saved game
                </Btn>
              )}
            </ErrorState>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-bm-border px-5 py-4">
          <div>
            {editable && editor.game && (
              <Btn variant="danger" onClick={() => setConfirm({ kind: "delete" })} disabled={busy}>
                <Icon name="trash" size={14} className="mr-1.5" />
                Delete game
              </Btn>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Btn variant="secondary" onClick={() => leave(closeEditor)} disabled={busy}>
              {dirty ? "Cancel" : "Close"}
            </Btn>
            {editable && (
              <>
                <Btn variant="secondary" onClick={() => save("draft")} disabled={busy}>
                  {saving === "draft" ? "Saving..." : "Save draft"}
                </Btn>
                <Btn onClick={() => save("final")} disabled={busy}>
                  {saving === "final" ? "Saving..." : "Save final"}
                </Btn>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  } else {
    content = (
      <>
        {loadError && <ErrorState role="alert">{loadError.text}</ErrorState>}
        <GameList
          games={data.games || []}
          season={season}
          seasons={data.seasons || []}
          onSeasonChange={changeSeason}
          onOpen={(game) => openEditor(draftFromGame(game, roster), game)}
          onNew={() => openEditor(newDraft(roster), null)}
          canEdit={editable}
          rosterEmpty={!roster.length}
          loading={loading}
          blocked={blocked}
        />
        {!blocked && <SeasonTotals games={data.games || []} roster={roster} season={season} />}
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {header}
      {data && !blocked && <ScoreUpdateBar refresh={refresh} canEdit={editable} onRefresh={updateScores} starting={starting} />}
      {content}
      {confirm?.kind === "delete" && editor?.game && (
        <ConfirmModal
          title="Delete this game?"
          body={`The game against ${editor.game.opponent} on ${gameDateText(editor.game.date)} and every line in it will be removed. Player stats and scores update after.`}
          confirmLabel="Delete game"
          tone="danger"
          busy={deleting}
          onConfirm={removeGame}
          onClose={() => {
            if (!deleting) setConfirm(null);
          }}
        />
      )}
      {confirm?.kind === "leave" && (
        <ConfirmModal
          title="Leave without saving?"
          body="Your changes to this game are not saved yet."
          confirmLabel="Leave without saving"
          cancelLabel="Keep editing"
          requireAck={false}
          onConfirm={() => {
            const then = confirm.then;
            setConfirm(null);
            then();
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default TeamGamesPage;
